import '../css/app.css'
import '../css/common.css'
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { X, BookOpen, Plus } from 'lucide-react';
import dayjs from 'dayjs';
import { change_Current_Location, changeHeaderTitle } from '../../store';
import List from '../component/list/list';
import { MessageModal } from '../component/modal/modal';
import PageHelp from '../component/help/PageHelp';
import { changeNum } from '../common/function';

const STATUS_LABEL = {
    ALL: '전체',
    PENDING: '대기',
    PARTIAL: '차감중',
    COMPLETED: '완료',
    CARRIED_OVER: '이월',
    OFFLINE_COLLECTED: '수금완료',
    EXCLUDED: '미부과 제외',
};

const STATUS_OPTIONS_MAP = {
    '대기': 'PENDING',
    '차감중': 'PARTIAL',
    '완료': 'COMPLETED',
    '이월': 'CARRIED_OVER',
    '수금완료': 'OFFLINE_COLLECTED',
};

const STATUS_STYLE = {
    PENDING: { bg: '#fff3cd', color: '#856404' },
    PARTIAL: { bg: '#cce5ff', color: '#004085' },
    COMPLETED: { bg: '#d4edda', color: '#155724' },
    CARRIED_OVER: { bg: '#f8d7da', color: '#721c24' },
    OFFLINE_COLLECTED: { bg: '#e2e3e5', color: '#383d41' },
    EXCLUDED: { bg: '#e2e3e5', color: '#383d41' },
};

const SETTING_FILTER_MAP = {
    '부과': 'Y',
    '미부과': 'N',
};

const toolbarInputStyle = (width) => ({
    width,
    height: '30px',
    minHeight: '30px',
    padding: '0 10px',
    fontSize: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    boxSizing: 'border-box',
});

const toolbarButtonStyle = {
    height: '30px',
    minHeight: '30px',
    padding: '0 11px',
    fontSize: '12px',
    lineHeight: '28px',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
};

const safeNum = (value) => changeNum(Number(value) || 0);
const TERMINAL_UNIT_FEE = 11000;

const getManualTerminalCount = (row = {}) => {
    return Number(row.add_terminals ?? row.add_terminal_count ?? row.terminal_count ?? row.current_terminals ?? 0) || 0;
};

const getManualFeeAmount = (row = {}) => {
    const rawAmount = row.add_fee ?? row.expected_fee ?? row.estimated_fee ?? row.total_fee ?? row.fee_amount ?? row.amount;
    const amount = Number(rawAmount || 0);
    if (amount > 0) return amount;
    const terminalCount = getManualTerminalCount(row);
    return terminalCount > 0 ? terminalCount * TERMINAL_UNIT_FEE : 0;
};

const DEDUCTION_TYPE_LABEL = {
    PAYOUT: '정산차감',
    OFFLINE: '직접수령',
};

const TRANSFER_STATUS_LABEL = { Y: '완료', N: '대기', I: '처리중', F: '실패' };
const TRANSFER_STATUS_COLOR = { Y: '#155724', N: '#856404', I: '#004085', F: '#721c24' };

const tableThStyle = { padding: '7px 6px', borderBottom: '1px solid #d1d5db', background: '#f8fafc', color: '#334155', fontWeight: 700, textAlign: 'center' };
const tableTdStyle = { padding: '7px 6px', borderBottom: '1px solid #e5e7eb', color: '#334155' };

function FastTerminalFeePreview() {
    const dispatch = useDispatch();
    const MyData = useSelector((state) => state.MyData);
    const brand = useSelector((state) => state.brand);

    const [message, setMessage] = useState({ msg: '' });
    const [feeMonth, setFeeMonth] = useState(dayjs().format('YYYY-MM'));
    const [statusFilter, setStatusFilter] = useState('전체');
    const [settingFilter, setSettingFilter] = useState('부과');
    const [selectedValue, setSelectedValue] = useState('MID');
    const [selectedText, setSelectedText] = useState('');
    const [page, setPage] = useState(1);
    const [maxFirstPage] = useState(30);
    const [listData, setListData] = useState({});
    const [listRows, setListRows] = useState([]);
    const [spinner, setSpinner] = useState(false);
    const [reload, setReload] = useState(false);
    const [clickData, setClickData] = useState(null);
    const [detailModal, setDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [merchantModal, setMerchantModal] = useState(false);
    const [merchantData, setMerchantData] = useState(null);
    const [manualModal, setManualModal] = useState(false);
    const [manualMonth, setManualMonth] = useState(dayjs().format('YYYY-MM'));
    const [manualPreview, setManualPreview] = useState(null);
    const [manualLoading, setManualLoading] = useState(false);
    const [guideModal, setGuideModal] = useState(false);

    useEffect(() => {
        dispatch(change_Current_Location('단말기비 관리'));
        dispatch(changeHeaderTitle({
            mainTitle: '관리자',
            subTitle: "'빠른' 단말기비 관리"
        }));
    }, [dispatch]);

    const isAllowed = brand?.key === 'fast' && MyData?.role === 'SUPER';

    useEffect(() => {
        if (!isAllowed) return;
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowed, page, reload]);

    const fetchList = async () => {
        if (page === 1) setSpinner(true);
        try {
            const params = { page, limit: maxFirstPage };
            if (feeMonth) params.fee_month = feeMonth;
            const settingValue = SETTING_FILTER_MAP[settingFilter] || '';
            if (settingValue) params.terminal_fee_yn = settingValue;
            const statusValue = STATUS_OPTIONS_MAP[statusFilter] || '';
            if (statusValue) params.status = statusValue;
            if (selectedText.trim()) {
                if (selectedValue === '가맹점명') params.co_nm = selectedText.trim();
                else params.mid = selectedText.trim();
            }
            const response = await axios.get(process.env.REACT_APP_API + '/api/merchant-terminal-fee', {
                withCredentials: true,
                params,
            });
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                const nextRows = tmp?.data?.list ?? [];
                setListData(tmp?.data ?? {});
                setListRows(page === 1 ? nextRows : (prev) => [...prev, ...nextRows]);
                setClickData(null);
            } else {
                setListData({});
                setListRows([]);
                setMessage({ msg: tmp?.msg || '단말기비 목록 조회에 실패했습니다.' });
            }
        } catch (error) {
            console.error(error);
            setListData({});
            setListRows([]);
            setMessage({ msg: error?.response?.data?.msg || '단말기비 목록 조회에 실패했습니다. API 권한 또는 서버 응답을 확인해주세요.' });
        } finally {
            setSpinner(false);
        }
    };

    const filteredRows = listRows;

    const summary = useMemo(() => {
        const apiSummary = listData?.summary;
        const enabled = listRows.filter((row) => row.terminal_fee_yn !== 'N');
        const disabled = listRows.filter((row) => row.terminal_fee_yn === 'N');
        const charged = enabled.filter((row) => row.status !== 'PENDING');
        const newCharge = enabled.filter((row) => row.status === 'PENDING');
        const remaining = enabled.reduce((sum, row) => sum + Number(row.remaining_amount || 0), 0);
        return {
            enabled: apiSummary?.enabled_count ?? enabled.length,
            disabled: apiSummary?.disabled_count ?? disabled.length,
            charged: apiSummary?.total_count ?? charged.length,
            newCharge: apiSummary?.pending_count ?? newCharge.length,
            remaining: apiSummary?.remaining_sum ?? remaining,
        };
    }, [listData, listRows]);

    const tableHeaderData = [
        { no: 0, text: '부과월', width: '80px', textAlign: 'center' },
        { no: 1, text: 'MID', width: '100px', textAlign: 'center' },
        { no: 2, text: '가맹점명', width: '150px', textAlign: 'center' },
        { no: 3, text: '단말기수', width: '70px', textAlign: 'center' },
        { no: 4, text: '단말기비', width: '90px', textAlign: 'center' },
        { no: 5, text: '차감액', width: '90px', textAlign: 'center' },
        { no: 6, text: '잔액', width: '90px', textAlign: 'center' },
        { no: 7, text: '부과설정', width: '90px', textAlign: 'center' },
        { no: 8, text: '상태', width: '90px', textAlign: 'center' },
        { no: 9, text: '메모', width: '180px', textAlign: 'center' },
    ];

    const tableBodyData = useMemo(() => {
        return filteredRows.map((row) => {
            const statusStyle = STATUS_STYLE[row.status] || {};
            const rowArr = [
                { no: 0, text: row.fee_month, width: '80px', textAlign: 'center' },
                { no: 1, text: row.mid, width: '100px', textAlign: 'center' },
                { no: 2, text: row.co_nm, width: '150px', textAlign: 'left' },
                { no: 3, text: String(row.terminal_count), width: '70px', textAlign: 'center' },
                { no: 4, text: row.terminal_fee_yn === 'Y' ? `${changeNum(row.total_fee)}원` : '-', width: '90px', textAlign: 'right' },
                { no: 5, text: row.terminal_fee_yn === 'Y' ? `${changeNum(row.deducted_amount)}원` : '-', width: '90px', textAlign: 'right' },
                {
                    no: 6,
                    text: row.terminal_fee_yn === 'Y' ? `${changeNum(row.remaining_amount)}원` : '-',
                    width: '90px',
                    textAlign: 'right',
                    style: { color: Number(row.remaining_amount) > 0 ? '#dc3545' : '#155724', fontWeight: '600' },
                },
                {
                    no: 7,
                    text: <SettingChip enabled={row.terminal_fee_yn === 'Y'} />,
                    width: '90px',
                    textAlign: 'center',
                },
                {
                    no: 8,
                    text: <StatusChip label={STATUS_LABEL[row.status]} styleMap={statusStyle} />,
                    width: '90px',
                    textAlign: 'center',
                },
                { no: 9, text: (row.memo ?? row.admin_memo) || '-', width: '180px', textAlign: 'left' },
            ];
            rowArr.id = row.id;
            rowArr._raw = row;
            return rowArr;
        });
    }, [filteredRows]);

    const runSearch = () => {
        setPage(1);
        setReload((prev) => !prev);
    };

    const handleThisMonth = () => {
        setFeeMonth(dayjs().format('YYYY-MM'));
        setPage(1);
        setReload((prev) => !prev);
    };

    const handlePrevMonth = () => {
        setFeeMonth(dayjs(feeMonth + '-01').subtract(1, 'month').format('YYYY-MM'));
        setPage(1);
        setReload((prev) => !prev);
    };

    const onSubmitSearch = () => runSearch();

    const fetchDetail = async (id) => {
        if (!id) {
            setMessage({ msg: '아직 생성된 단말기비 부과건이 없는 가맹점입니다.' });
            return;
        }
        try {
            const response = await axios.get(process.env.REACT_APP_API + '/api/merchant-terminal-fee/' + id, {
                withCredentials: true,
            });
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setDetailData({ ...(tmp?.data?.fee ?? {}), deductionLogs: tmp?.data?.deductionLogs ?? [] });
                setDetailModal(true);
            } else {
                setMessage({ msg: tmp?.msg || '단말기비 상세 조회에 실패했습니다.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: error?.response?.data?.msg || '단말기비 상세 조회에 실패했습니다.' });
        }
    };

    const fetchMerchantHistory = async (mid) => {
        if (!mid) return;
        try {
            const response = await axios.get(process.env.REACT_APP_API + '/api/merchant-terminal-fee/merchant/' + mid, {
                withCredentials: true,
            });
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setMerchantData(tmp?.data);
                setMerchantModal(true);
            } else {
                setMessage({ msg: tmp?.msg || '가맹점별 이력 조회에 실패했습니다.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: error?.response?.data?.msg || '가맹점별 이력 조회에 실패했습니다.' });
        }
    };

    const fetchManualPreview = async (month) => {
        const currentMonth = dayjs().format('YYYY-MM');
        if (month > currentMonth) {
            setMessage({ msg: '현재월 이후 월은 수동 부과 미리보기를 조회할 수 없습니다.' });
            return;
        }
        setManualLoading(true);
        setManualPreview(null);
        try {
            const response = await axios.get(process.env.REACT_APP_API + '/api/merchant-terminal-fee/manual-preview', {
                withCredentials: true,
                params: { fee_month: month },
            });
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setManualPreview(tmp?.data);
            } else {
                setMessage({ msg: tmp?.msg || '수동 부과 미리보기 조회에 실패했습니다.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: error?.response?.data?.msg || '수동 부과 미리보기 조회에 실패했습니다.' });
        } finally {
            setManualLoading(false);
        }
    };

    const onRowClick = (rowArr) => {
        setClickData(rowArr);
        fetchDetail(rowArr?._raw?.id);
    };

    if (!isAllowed) {
        return (
            <div className="content" id="contents">
                <div className="intro-y box" style={{ padding: '20px', fontSize: '13px' }}>
                    '빠른' SUPER 계정에서만 접근 가능한 단말기비 관리 화면입니다.
                </div>
            </div>
        );
    }

    const selected = clickData?._raw ?? filteredRows[0];

    return (
        <div className="content" id="contents" style={{ display: 'flex', flexDirection: 'column' }}>
            <MessageModal message={message} setMessage={setMessage} />

            <div className="intro-y box" style={{ padding: '14px 20px', marginBottom: '12px', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#1e293b' }}>단말기비 관리</h2>
                            <PageHelp
                                tone="blue"
                                width="540px"
                                sections={[
                                    {
                                        title: "설명",
                                        items: [
                                            "'빠른' 가맹점의 단말기비 부과 대상 여부와 월별 부과 상태를 확인하는 화면입니다.",
                                            "수동부과는 자동으로 생성되지 않았거나 추가 반영이 필요한 월 단말기비를 운영자가 직접 생성하는 기능입니다.",
                                            "가맹점 수정 화면의 이번달부터 부과는 특정 가맹점 1건을 부과 대상으로 전환하는 기능이고, 이 화면의 수동부과는 부과월 기준 대상 건을 모아 처리하는 기능입니다.",
                                        ],
                                    },
                                    {
                                        title: "가이드",
                                        items: [
                                            "부과월과 부과/미부과 조건을 선택해 대상 가맹점을 조회합니다.",
                                            "가맹점 행을 선택해 상세와 월별 이력을 확인합니다.",
                                            "수동부과가 필요하면 미리보기로 대상 가맹점과 금액을 먼저 확인합니다.",
                                            "필요한 가맹점만 선택한 뒤 수동부과를 실행합니다.",
                                        ],
                                    },
                                    {
                                        title: "주의사항",
                                        items: [
                                            "이번달부터 부과를 선택하면 부과건 생성과 즉시 차감이 시도될 수 있습니다.",
                                            "수동부과 실행 전 대상 가맹점, 부과월, 금액을 반드시 확인합니다.",
                                            "이미 생성된 부과건은 가맹점 설정을 미부과로 바꿔도 자동 취소되지 않습니다.",
                                            "잘못 생성된 부과건은 상세 화면에서 상태를 확인한 뒤 부과취소 가능 여부를 확인합니다.",
                                        ],
                                    },
                                ]}
                            />
                        </div>
                        <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>'빠른' 가맹점의 단말기비 부과 설정과 월별 상태를 관리합니다.</div>
                    </div>
                </div>
                <form id="tabulator-html-filter-form" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                                type="month"
                                className="form-control"
                                style={toolbarInputStyle('150px')}
                                value={feeMonth}
                                max={dayjs().format('YYYY-MM')}
                                onChange={(e) => {
                                    setFeeMonth(e.target.value);
                                    setPage(1);
                                    setReload((prev) => !prev);
                                }}
                            />
                            <button type="button" className="btn searchPayBtn" style={toolbarButtonStyle} onClick={handleThisMonth}>당월</button>
                            <button type="button" className="btn searchPayBtn" style={toolbarButtonStyle} onClick={handlePrevMonth}>전월</button>
                        </div>

                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                                setReload((prev) => !prev);
                            }}
                            style={toolbarInputStyle('120px')}
                        >
                            {['전체', '대기', '차감중', '완료', '이월', '수금완료'].map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>

                        <select
                            className="form-select"
                            value={settingFilter}
                            onChange={(e) => {
                                setSettingFilter(e.target.value);
                                setPage(1);
                                setReload((prev) => !prev);
                            }}
                            style={toolbarInputStyle('120px')}
                        >
                            {['부과', '미부과'].map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <select
                                className="form-select"
                                value={selectedValue}
                                onChange={(e) => setSelectedValue(e.target.value)}
                                style={toolbarInputStyle('110px')}
                            >
                                {['MID', '가맹점명'].map((item) => <option key={item} value={item}>{item}</option>)}
                            </select>
                            <input
                                type="text"
                                className="form-control"
                                value={selectedText}
                                onChange={(e) => setSelectedText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        onSubmitSearch();
                                    }
                                }}
                                placeholder="MID 또는 가맹점명"
                                style={toolbarInputStyle('220px')}
                            />
                            <button type="button" className="btn btn-primary searchPayBtn" style={{ ...toolbarButtonStyle, minWidth: '48px' }} onClick={onSubmitSearch}>검색</button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                        <button
                            type="button"
                            className="btn btn-primary btn_small"
                            onClick={() => {
                                setManualMonth(feeMonth);
                                setManualPreview(null);
                                setManualModal(true);
                            }}
                            style={{ ...toolbarButtonStyle, display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            <Plus size={14} />
                            수동부과 미리보기
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn_small"
                            onClick={() => setGuideModal(true)}
                            style={{ ...toolbarButtonStyle, display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            <BookOpen size={14} />
                            가이드
                        </button>
                    </div>
                </form>

                <div style={{
                    marginTop: '12px',
                    padding: '9px 12px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#334155',
                }}>
                    '빠른'은 기본 미부과입니다. 단말기비 부과가 켜진 가맹점만 월별 부과 대상입니다.
                </div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #e5e7eb', fontSize: '12px' }}>
                    <SummaryItem label="부과설정 ON" value={`${summary.enabled}개`} color="#004085" />
                    <SummaryItem label="미부과" value={`${summary.disabled}개`} color="#721c24" />
                    <SummaryItem label="이번달 부과" value={`${summary.charged}건`} color="#155724" />
                    <SummaryItem label="신규 부과 가능" value={`${summary.newCharge}건`} color="#856404" />
                    <SummaryItem label="잔액" value={`${changeNum(summary.remaining)}원`} color="#dc3545" />
                </div>
            </div>

            <div className="intro-y box intro-padding">
                <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', minHeight: 'calc(100dvh - 290px)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <List
                            tableHeaderData={tableHeaderData}
                            tableBodyData={listData?.totCnt ?? filteredRows.length}
                            filter_tableBodyData={tableBodyData}
                            page={page}
                            setPage={setPage}
                            maxFirstPage={maxFirstPage}
                            nonePage={false}
                            scrollState={true}
                            height="calc(100vh - 330px)"
                            clickData={clickData}
                            onRowClick={onRowClick}
                            spinner={spinner}
                        />
                    </div>
                    <div style={{ width: '360px', flexShrink: 0 }}>
                        <DetailPanel
                            data={selected}
                            setMessage={setMessage}
                            onSaved={() => {
                                setPage(1);
                                setReload((prev) => !prev);
                            }}
                        />
                    </div>
                </div>
            </div>

            {detailModal && detailData && (
                <FeeDetailModal
                    data={detailData}
                    onClose={() => setDetailModal(false)}
                    onMerchantHistory={fetchMerchantHistory}
                    setMessage={setMessage}
                />
            )}

            {merchantModal && merchantData && (
                <MerchantHistoryModal
                    data={merchantData}
                    onClose={() => setMerchantModal(false)}
                />
            )}

            {manualModal && (
                <ManualPreviewModal
                    month={manualMonth}
                    setMonth={setManualMonth}
                    preview={manualPreview}
                    loading={manualLoading}
                    onPreview={() => fetchManualPreview(manualMonth)}
                    onClose={() => setManualModal(false)}
                    setMessage={setMessage}
                />
            )}

            {guideModal && (
                <ModalOverlay onClose={() => setGuideModal(false)}>
                    <div style={{ width: '520px' }}>
                        <ModalTitle title="'빠른' 단말기비 운영 가이드" onClose={() => setGuideModal(false)} />
                        <GuideBody />
                    </div>
                </ModalOverlay>
            )}
        </div>
    );
}

function FeeDetailModal({ data, onClose, onMerchantHistory, setMessage }) {
    const statusStyle = STATUS_STYLE[data.status] || {};
    const [actionPanel, setActionPanel] = useState('');
    const [memoText, setMemoText] = useState(data.admin_memo || '');
    const [offlineMemo, setOfflineMemo] = useState('');
    const canCancel = data.status === 'PENDING' && Number(data.deducted_amount || 0) === 0;
    const canOfflineCollect = Number(data.remaining_amount || 0) > 0 && !['COMPLETED', 'OFFLINE_COLLECTED'].includes(data.status);

    const previewOnly = (label) => {
        setMessage({ msg: label + ' UI 확인용입니다. 아직 저장 API는 연결하지 않았습니다.' });
    };

    return (
        <ModalOverlay onClose={onClose}>
            <div style={{ width: '660px', maxHeight: '80vh', overflow: 'auto' }}>
                <ModalTitle title="단말기비 상세" onClose={onClose} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', marginBottom: '16px' }}>
                    <InfoRow label="부과월" value={data.fee_month || '-'} />
                    <InfoRow label="MID" value={data.mid || '-'} />
                    <InfoRow label="가맹점명" value={data.co_nm || '-'} />
                    <InfoRow label="단말기수" value={data.terminal_count ?? '-'} />
                    <InfoRow label="단말기비" value={safeNum(data.total_fee) + '원'} />
                    <InfoRow label="이월금액" value={safeNum(data.carried_over_amount) + '원'} />
                    <InfoRow label="총부과액" value={safeNum(data.total_due) + '원'} />
                    <InfoRow label="차감액" value={safeNum(data.deducted_amount) + '원'} />
                    <InfoRow label="잔액" value={safeNum(data.remaining_amount) + '원'} bold color={Number(data.remaining_amount) > 0 ? '#dc3545' : '#155724'} />
                    <InfoRow label="상태" value={<StatusChip label={STATUS_LABEL[data.status] || data.status || '-'} styleMap={statusStyle} />} />
                    {data.admin_memo && <InfoRow label="메모" value={data.admin_memo} />}
                </div>

                <SimpleLogTable logs={data.deductionLogs ?? []} />

                {actionPanel && (
                    <div style={{ marginTop: '14px', padding: '12px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#f8fafc' }}>
                        {actionPanel === 'memo' && (
                            <ActionPanel title="메모 수정" description="부과건에 남길 관리자 메모입니다.">
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    value={memoText}
                                    onChange={(e) => setMemoText(e.target.value)}
                                    placeholder="관리자 메모"
                                    style={{ fontSize: '12px', resize: 'vertical' }}
                                />
                                <ActionPanelFooter onCancel={() => setActionPanel('')} onConfirm={() => previewOnly('메모 수정')} confirmText="메모 저장" />
                            </ActionPanel>
                        )}
                        {actionPanel === 'offline' && (
                            <ActionPanel title="오프라인 수금 처리" description="현금, 별도 계좌이체 등 시스템 외 방식으로 수금한 경우 사용합니다.">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                                    <PanelField label="현재 잔액" value={safeNum(data.remaining_amount) + '원'} />
                                    <PanelField label="처리 후 상태" value="수금완료" />
                                </div>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={offlineMemo}
                                    onChange={(e) => setOfflineMemo(e.target.value)}
                                    placeholder="수금 메모"
                                    style={{ fontSize: '12px', resize: 'vertical' }}
                                />
                                <ActionPanelFooter onCancel={() => setActionPanel('')} onConfirm={() => previewOnly('오프라인 수금')} confirmText="수금 처리" />
                            </ActionPanel>
                        )}
                        {actionPanel === 'cancel' && (
                            <ActionPanel title="부과 취소" description="잘못 생성된 부과건을 취소합니다. 차감이 진행된 건은 취소할 수 없습니다." danger>
                                <div style={{ padding: '9px 10px', border: '1px solid #fecaca', background: '#fff1f2', color: '#991b1b', borderRadius: '4px', fontSize: '12px', lineHeight: 1.5 }}>
                                    이 부과건을 취소하면 목록에서 제거되거나 취소 상태로 처리됩니다. 실제 취소 전 한 번 더 확인이 필요합니다.
                                </div>
                                <ActionPanelFooter onCancel={() => setActionPanel('')} onConfirm={() => previewOnly('부과 취소')} confirmText="부과 취소" danger />
                            </ActionPanel>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button" className="btn searchPayBtn" style={{ height: '32px', fontSize: '12px' }} onClick={() => onMerchantHistory(data.mid)}>
                            가맹점 이력
                        </button>
                        <button type="button" className="btn searchPayBtn" style={{ height: '32px', fontSize: '12px' }} onClick={() => setActionPanel(actionPanel === 'memo' ? '' : 'memo')}>
                            메모 수정
                        </button>
                        <button
                            type="button"
                            className="btn searchPayBtn"
                            style={{ height: '32px', fontSize: '12px', opacity: canOfflineCollect ? 1 : 0.45 }}
                            disabled={!canOfflineCollect}
                            onClick={() => setActionPanel(actionPanel === 'offline' ? '' : 'offline')}
                        >
                            오프라인 수금
                        </button>
                        <button
                            type="button"
                            className="btn"
                            style={{ height: '32px', fontSize: '12px', color: '#dc3545', border: '1px solid #dc3545', opacity: canCancel ? 1 : 0.45 }}
                            disabled={!canCancel}
                            onClick={() => setActionPanel(actionPanel === 'cancel' ? '' : 'cancel')}
                        >
                            부과 취소
                        </button>
                    </div>
                    <button type="button" className="btn btn-primary" style={{ height: '32px', fontSize: '12px' }} onClick={onClose}>
                        확인
                    </button>
                </div>
            </div>
        </ModalOverlay>
    );
}

function ActionPanel({ title, description, danger = false, children }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: danger ? '#991b1b' : '#111827' }}>{title}</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{description}</div>
            </div>
            {children}
        </div>
    );
}

function ActionPanelFooter({ onCancel, onConfirm, confirmText, danger = false }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '2px' }}>
            <button type="button" className="btn searchPayBtn" style={{ height: '30px', fontSize: '12px' }} onClick={onCancel}>취소</button>
            <button
                type="button"
                className={danger ? 'btn' : 'btn btn-primary'}
                style={{ height: '30px', fontSize: '12px', ...(danger ? { color: '#dc3545', border: '1px solid #dc3545' } : {}) }}
                onClick={onConfirm}
            >
                {confirmText}
            </button>
        </div>
    );
}

function MerchantHistoryModal({ data, onClose }) {
    const title = '가맹점 이력 - ' + (data.merchant?.co_nm || '-') + ' (' + (data.merchant?.mid || '-') + ')';
    return (
        <ModalOverlay onClose={onClose}>
            <div style={{ width: '680px', maxHeight: '80vh', overflow: 'auto' }}>
                <ModalTitle title={title} onClose={onClose} />
                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                    <SummaryItem label="총 부과액" value={safeNum(data.summary?.totalDue) + '원'} />
                    <SummaryItem label="총 차감액" value={safeNum(data.summary?.totalDeducted) + '원'} color="#004085" />
                    <SummaryItem label="총 잔액" value={safeNum(data.summary?.totalRemaining) + '원'} color="#dc3545" />
                </div>

                <h4 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 8px' }}>월별 단말기비</h4>
                <SimpleFeeTable rows={data.fees ?? []} />

                <h4 style={{ fontSize: '13px', fontWeight: 700, margin: '16px 0 8px' }}>차감 로그</h4>
                <SimpleLogTable logs={data.deductionLogs ?? []} />
            </div>
        </ModalOverlay>
    );
}

function ManualPreviewModal({ month, setMonth, preview, loading, onPreview, onClose, setMessage }) {
    const rows = preview?.list ?? [];
    const [checkedMids, setCheckedMids] = useState([]);
    const [rowMemos, setRowMemos] = useState({});

    useEffect(() => {
        setCheckedMids(rows.map((row) => row.mid).filter(Boolean));
        setRowMemos(rows.reduce((acc, row) => {
            if (row.mid) acc[row.mid] = row.memo || row.reason || '';
            return acc;
        }, {}));
    }, [preview]);

    const selectedRows = rows.filter((row) => checkedMids.includes(row.mid));
    const selectedAmount = selectedRows.reduce((sum, row) => sum + getManualFeeAmount(row), 0);
    const allChecked = rows.length > 0 && checkedMids.length === rows.length;

    const toggleAll = (checked) => {
        setCheckedMids(checked ? rows.map((row) => row.mid).filter(Boolean) : []);
    };

    const toggleRow = (mid, checked) => {
        if (!mid) return;
        setCheckedMids((prev) => checked ? [...new Set([...prev, mid])] : prev.filter((item) => item !== mid));
    };

    const updateRowMemo = (mid, value) => {
        setRowMemos((prev) => ({ ...prev, [mid]: value }));
    };


    const handleManualChargePreview = () => {
        if (checkedMids.length === 0) {
            setMessage({ msg: '수동 부과할 가맹점을 선택해주세요.' });
            return;
        }
        setMessage({ msg: '수동부과 실행 UI 확인용입니다. 행별 메모는 현재 API 형식에 맞춰 서버 협의가 필요합니다.' });
    };

    return (
        <ModalOverlay onClose={onClose}>
            <div style={{ width: '760px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                <ModalTitle title="수동 부과 미리보기" onClose={onClose} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexShrink: 0 }}>
                    <input type="month" className="form-control" style={toolbarInputStyle('150px')} value={month} max={dayjs().format('YYYY-MM')} onChange={(e) => setMonth(e.target.value)} />
                    <button type="button" className="btn btn-primary" style={{ height: '30px', fontSize: '12px' }} onClick={onPreview} disabled={loading}>
                        {loading ? '조회 중' : '미리보기 조회'}
                    </button>
                </div>

                {preview && (
                    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '12px', color: '#64748b', marginBottom: '10px', flexShrink: 0 }}>
                        <SummaryItem label="조회 대상" value={rows.length + '건'} />
                        <SummaryItem label="선택" value={checkedMids.length + '건'} color="#155724" />
                        <SummaryItem label="선택 예상금액" value={safeNum(selectedAmount) + '원'} color="#004085" />
                    </div>
                )}

                <div style={{ overflow: 'auto', minHeight: 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                            <tr>
                                <th style={{ ...tableThStyle, width: '38px' }}>
                                    <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} disabled={rows.length === 0} />
                                </th>
                                <th style={tableThStyle}>MID</th>
                                <th style={tableThStyle}>가맹점명</th>
                                <th style={tableThStyle}>모드</th>
                                <th style={tableThStyle}>단말기수</th>
                                <th style={tableThStyle}>예상금액</th>
                                <th style={{ ...tableThStyle, width: '220px' }}>개별 메모</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td style={{ ...tableTdStyle, textAlign: 'center' }} colSpan={7}>미리보기 결과가 없습니다.</td></tr>
                            ) : rows.map((row) => (
                                <tr key={row.mid}>
                                    <td style={{ ...tableTdStyle, textAlign: 'center' }}>
                                        <input type="checkbox" checked={checkedMids.includes(row.mid)} onChange={(e) => toggleRow(row.mid, e.target.checked)} />
                                    </td>
                                    <td style={tableTdStyle}>{row.mid}</td>
                                    <td style={tableTdStyle}>{row.co_nm || '-'}</td>
                                    <td style={{ ...tableTdStyle, textAlign: 'center' }}>{row.mode || '-'}</td>
                                    <td style={{ ...tableTdStyle, textAlign: 'right' }}>{getManualTerminalCount(row) || '-'}</td>
                                    <td style={{ ...tableTdStyle, textAlign: 'right' }}>{safeNum(getManualFeeAmount(row))}원</td>
                                    <td style={tableTdStyle}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ height: '28px', fontSize: '12px' }}
                                            value={rowMemos[row.mid] || ''}
                                            onChange={(e) => updateRowMemo(row.mid, e.target.value)}
                                            placeholder="개별 메모"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {preview && rows.length > 0 && (
                    <div style={{ flexShrink: 0, borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                            메모는 표의 가맹점별 입력칸에 직접 작성합니다.
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                            <button type="button" className="btn searchPayBtn" style={{ height: '32px', fontSize: '12px' }} onClick={onClose}>취소</button>
                            <button type="button" className="btn btn-primary" style={{ height: '32px', fontSize: '12px' }} onClick={handleManualChargePreview}>
                                선택 {checkedMids.length}건 수동부과
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </ModalOverlay>
    );
}

function SimpleFeeTable({ rows }) {
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
                <tr>
                    <th style={tableThStyle}>부과월</th>
                    <th style={tableThStyle}>단말기수</th>
                    <th style={tableThStyle}>총부과액</th>
                    <th style={tableThStyle}>차감액</th>
                    <th style={tableThStyle}>잔액</th>
                    <th style={tableThStyle}>상태</th>
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 ? (
                    <tr><td style={{ ...tableTdStyle, textAlign: 'center' }} colSpan={6}>이력이 없습니다.</td></tr>
                ) : rows.map((row, idx) => {
                    const statusStyle = STATUS_STYLE[row.status] || {};
                    return (
                        <tr key={row.id || idx}>
                            <td style={tableTdStyle}>{row.fee_month}</td>
                            <td style={{ ...tableTdStyle, textAlign: 'right' }}>{row.terminal_count ?? '-'}</td>
                            <td style={{ ...tableTdStyle, textAlign: 'right' }}>{safeNum(row.total_due)}원</td>
                            <td style={{ ...tableTdStyle, textAlign: 'right' }}>{safeNum(row.deducted_amount)}원</td>
                            <td style={{ ...tableTdStyle, textAlign: 'right' }}>{safeNum(row.remaining_amount)}원</td>
                            <td style={{ ...tableTdStyle, textAlign: 'center' }}><StatusChip label={STATUS_LABEL[row.status] || row.status || '-'} styleMap={statusStyle} /></td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function SimpleLogTable({ logs }) {
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
                <tr>
                    <th style={tableThStyle}>유형</th>
                    <th style={tableThStyle}>정산ID</th>
                    <th style={tableThStyle}>차감액</th>
                    <th style={tableThStyle}>잔액</th>
                    <th style={tableThStyle}>이체</th>
                    <th style={tableThStyle}>일시</th>
                </tr>
            </thead>
            <tbody>
                {logs.length === 0 ? (
                    <tr><td style={{ ...tableTdStyle, textAlign: 'center' }} colSpan={6}>차감 로그가 없습니다.</td></tr>
                ) : logs.map((log, idx) => (
                    <tr key={log.id || idx}>
                        <td style={tableTdStyle}>{DEDUCTION_TYPE_LABEL[log.deduction_type] || log.deduction_type || '-'}</td>
                        <td style={tableTdStyle}>{log.payout_id ?? '-'}</td>
                        <td style={{ ...tableTdStyle, textAlign: 'right' }}>{safeNum(log.deduction_amount)}원</td>
                        <td style={{ ...tableTdStyle, textAlign: 'right' }}>{safeNum(log.remaining_after)}원</td>
                        <td style={{ ...tableTdStyle, textAlign: 'center', color: TRANSFER_STATUS_COLOR[log.payout_transfer_status] || '#334155', fontWeight: 600 }}>{TRANSFER_STATUS_LABEL[log.payout_transfer_status] || log.payout_transfer_status || '-'}</td>
                        <td style={tableTdStyle}>{log.created_at || '-'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function DetailPanel({ data, setMessage, onSaved }) {
    const [enabled, setEnabled] = useState(data?.terminal_fee_yn === "Y");
    const [applyPolicy, setApplyPolicy] = useState("NEXT_MONTH");
    const [saving, setSaving] = useState(false);

    const wasEnabled = data?.terminal_fee_yn === "Y";
    const showApplyPolicy = !wasEnabled && enabled;

    useEffect(() => {
        setEnabled(data?.terminal_fee_yn === "Y");
        setApplyPolicy("NEXT_MONTH");
    }, [data]);

    if (!data) return null;

    const buildSaveMessage = (responseData) => {
        const chargeLabels = {
            CREATED: "이번달 부과건 생성",
            ALREADY_EXISTS: "이번달 부과건 이미 존재",
            NO_ACTIVE_TERMINAL: "활성 단말기 없음",
            FLAG_ONLY: "부과 설정만 저장",
        };
        const deductionLabels = {
            DEDUCTED: "즉시 차감 완료",
            NO_ELIGIBLE_PAYOUT: "차감 가능 정산건 없음",
            WALLET_DEFERRED: "지갑 차감은 배치 대기",
            ALREADY_DEDUCTED_FROM_PAYOUT: "이미 정산 차감됨",
            NO_AVAILABLE_PAYOUT_AMOUNT: "차감 가능 금액 없음",
            NO_REMAINING_AMOUNT: "잔액 없음",
            NOT_ATTEMPTED: "즉시 차감 미시도",
        };
        const parts = [];
        const chargeAction = responseData?.charge?.action;
        const deductionAction = responseData?.deduction?.action;
        if (chargeAction) parts.push(chargeLabels[chargeAction] || chargeAction);
        if (deductionAction) parts.push(deductionLabels[deductionAction] || deductionAction);
        return parts.length ? "단말기비 설정: " + parts.join(" / ") : "단말기비 설정이 저장되었습니다.";
    };

    const handleSave = async () => {
        if (enabled === wasEnabled) {
            setMessage({ msg: "변경된 단말기비 설정이 없습니다." });
            return;
        }

        setSaving(true);
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + "/api/merchant-terminal-fee/exemption",
                {
                    mid: data.mid,
                    apply_policy: enabled ? applyPolicy : "NONE",
                },
                { withCredentials: true }
            );
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setMessage({ msg: buildSaveMessage(tmp?.data) });
                onSaved?.();
            } else {
                setMessage({ msg: tmp?.msg || "단말기비 설정 저장에 실패했습니다." });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: error?.response?.data?.msg || "단말기비 설정 저장에 실패했습니다. API 권한 또는 서버 응답을 확인해주세요." });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: "5px" }}>
            <div className="modal-dialog">
                <div className="marginTop30 modal_sty" style={{ fontSize: "12px" }}>
                    <div className="modal-header">
                        <h2 className="font-medium text-base mr-auto modal_title_sty">가맹점 단말기비 설정</h2>
                    </div>
                    <div className="modal-body grid grid-cols-12 gap-4 gap-y-3">
                        <PanelField label="MID" value={data.mid} />
                        <PanelField label="가맹점명" value={data.co_nm} />
                        <PanelField label="단말기수" value={(data.terminal_count ?? 0) + "개"} />
                        <PanelField label="부과월" value={data.fee_month} />
                        <div className="col-span-12">
                            <label className="form-label">단말기비 부과</label>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={enabled}
                                    onChange={(e) => setEnabled(e.target.checked)}
                                    style={{ width: "14px", height: "14px" }}
                                />
                                {enabled ? "정산금에서 차감" : <span style={{ color: "#dc3545", fontWeight: "600" }}>미부과</span>}
                            </label>

                            {showApplyPolicy && (
                                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            name="fast_terminal_fee_apply_policy"
                                            checked={applyPolicy === "CURRENT_MONTH"}
                                            onChange={() => setApplyPolicy("CURRENT_MONTH")}
                                        />
                                        이번달부터 부과
                                    </label>
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            name="fast_terminal_fee_apply_policy"
                                            checked={applyPolicy === "NEXT_MONTH"}
                                            onChange={() => setApplyPolicy("NEXT_MONTH")}
                                        />
                                        다음달부터 부과
                                    </label>
                                </div>
                            )}

                            <div style={{ marginTop: "8px", padding: "7px 8px", border: "1px solid #e5e7eb", borderRadius: "4px", background: "#f8fafc", color: "#64748b", fontSize: "11px", lineHeight: 1.5 }}>
                                {showApplyPolicy ? (
                                    applyPolicy === "CURRENT_MONTH"
                                        ? "이번달 부과건 생성을 요청합니다. 익일이체는 이체 전 정산건이면 즉시 차감을 시도하고, 지갑은 배치 차감 대기 상태가 됩니다."
                                        : "설정만 ON으로 저장하고 이번달 부과건은 만들지 않습니다. 다음 부과월부터 월별 단말기비 대상이 됩니다."
                                ) : enabled ? (
                                    "이미 부과 설정이 켜진 가맹점입니다. 적용 시점은 신규 ON 전환 시에만 선택합니다."
                                ) : (
                                    "미부과로 저장하면 부과 대상에서 제외됩니다. 이미 생성된 부과건은 자동 취소되지 않습니다."
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ height: "32px", fontSize: "12px" }}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "저장 중" : "설정 저장"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SettingChip({ enabled }) {
    return (
        <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: enabled ? '#dbeafe' : '#f8d7da',
            color: enabled ? '#1d4ed8' : '#721c24',
        }}>
            {enabled ? 'ON' : 'OFF 제외'}
        </span>
    );
}

function StatusChip({ label, styleMap }) {
    return (
        <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: styleMap.bg,
            color: styleMap.color,
        }}>
            {label}
        </span>
    );
}

function SummaryItem({ label, value, color = '#111827' }) {
    return (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ color: '#64748b' }}>{label}</span>
            <strong style={{ color }}>{value}</strong>
        </div>
    );
}

function PanelField({ label, value }) {
    return (
        <div className="col-span-12 sm:col-span-6">
            <label className="form-label">{label}</label>
            <input type="text" className="form-control input-padding" value={value ?? ''} readOnly />
        </div>
    );
}

function ModalOverlay({ children, onClose }) {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
            }}
            onClick={onClose}
        >
            <div
                className="box"
                style={{
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    padding: '20px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}

function ModalTitle({ title, onClose }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>{title}</h3>
            <button
                type="button"
                onClick={onClose}
                style={{ border: 0, background: 'transparent', padding: '4px', cursor: 'pointer' }}
            >
                <X size={18} />
            </button>
        </div>
    );
}

function InfoRow({ label, value, bold = false, color }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ color: '#64748b' }}>{label}</span>
            <span style={{ fontWeight: bold ? '700' : '500', color }}>{value}</span>
        </div>
    );
}

function GuideBody() {
    return (
        <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.8 }}>
            <p style={{ margin: '0 0 10px' }}>
                '빠른' 단말기비는 기본 미부과입니다. 가맹점 수정 화면에서 단말기비 부과를 ON으로 저장한 가맹점만 월별 부과 대상이 됩니다.
            </p>
            <ol style={{ paddingLeft: '20px', margin: 0 }}>
                <li>부과 대상 설정: 가맹점 수정 화면에서 ON/OFF와 이번달/다음달 적용 시점을 결정합니다.</li>
                <li>이번달부터 부과: 가맹점 저장 시 현재 부과월 건 생성을 요청합니다.</li>
                <li>다음달부터 부과: 설정만 저장하고 다음 부과월부터 대상에 포함합니다.</li>
                <li>이 화면은 생성된 부과건의 차감, 잔액, 수금, 메모를 관리합니다.</li>
            </ol>
        </div>
    );
}

export default FastTerminalFeePreview;
